import { findMeaningById, hasValidOutgoingRelation } from "../vocabulary/index.ts";
import type { Exercise, ExerciseSet, MeaningCard, VocabularySnapshot } from "../vocabulary/types.ts";

export const REQUIRED_EXERCISE_COUNT = 20;
export const MIX_WITH_RELATIONS = {
  context_recall: 14,
  meaning_to_word: 4,
  confusable_context: 2,
} as const;
export const MIX_WITHOUT_RELATIONS = {
  context_recall: 16,
  meaning_to_word: 4,
  confusable_context: 0,
} as const;

export interface ExerciseValidationIssue {
  code: string;
  message: string;
}

export function countByKind(exercises: readonly Exercise[]): Record<Exercise["kind"], number> {
  const counts = {
    context_recall: 0,
    meaning_to_word: 0,
    confusable_context: 0,
  };
  for (const exercise of exercises) {
    counts[exercise.kind] += 1;
  }
  return counts;
}

export function expectedMix(hasUsefulRelations: boolean) {
  return hasUsefulRelations ? MIX_WITH_RELATIONS : MIX_WITHOUT_RELATIONS;
}

function unique<T>(values: readonly T[]): boolean {
  return new Set(values).size === values.length;
}

export function validateExercise(exercise: Exercise): ExerciseValidationIssue[] {
  const issues: ExerciseValidationIssue[] = [];
  if (!exercise.question.trim()) {
    issues.push({ code: "empty_question", message: "Question text is required." });
  }
  if (!exercise.correctAnswer.trim()) {
    issues.push({ code: "empty_correct_answer", message: "Correct answer is required." });
  }
  if (exercise.acceptedAnswers.length === 0) {
    issues.push({ code: "no_accepted_answers", message: "At least one accepted answer is required." });
  }
  const acceptedNormalized = exercise.acceptedAnswers.map((item) => item.trim().toLowerCase().replace(/\s+/g, " "));
  if (!acceptedNormalized.includes(exercise.correctAnswer.trim().toLowerCase().replace(/\s+/g, " "))) {
    issues.push({
      code: "correct_not_accepted",
      message: "Correct answer must be included among accepted forms.",
    });
  }
  if (exercise.kind === "confusable_context") {
    if (exercise.options.length < 2 || exercise.options.length > 4) {
      issues.push({
        code: "option_count",
        message: "Confusable questions must have 2–4 options.",
      });
    }
    const optionIds = exercise.options.map((option) => option.id);
    if (!unique(optionIds)) {
      issues.push({ code: "duplicate_option_ids", message: "Option IDs must be unique." });
    }
    const optionLabels = exercise.options.map((option) =>
      option.label.trim().toLowerCase().replace(/\s+/g, " "),
    );
    if (!unique(optionLabels)) {
      issues.push({
        code: "duplicate_option_labels",
        message: "Answer options must not be normalized duplicates.",
      });
    }
    if (!optionIds.includes(exercise.correctOptionId)) {
      issues.push({
        code: "correct_option_missing",
        message: "Correct option ID must match one of the options.",
      });
    }
  }
  return issues;
}

export function questionsLookNearDuplicate(a: string, b: string): boolean {
  const compact = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const left = compact(a);
  const right = compact(b);
  if (!left || !right) {
    return false;
  }
  if (left === right) {
    return true;
  }
  const tokensA = new Set(left.split(" ").filter(Boolean));
  const tokensB = new Set(right.split(" ").filter(Boolean));
  const intersection = [...tokensA].filter((token) => tokensB.has(token)).length;
  const smaller = Math.min(tokensA.size, tokensB.size);
  return smaller > 0 && intersection / smaller >= 0.9 && Math.abs(tokensA.size - tokensB.size) <= 1;
}

export function validateExerciseSet(
  set: Pick<ExerciseSet, "exercises" | "meaningId">,
  snapshot: VocabularySnapshot,
  hasUsefulRelations: boolean,
): ExerciseValidationIssue[] {
  const issues: ExerciseValidationIssue[] = [];
  if (set.exercises.length !== REQUIRED_EXERCISE_COUNT) {
    issues.push({
      code: "exercise_count",
      message: `A ready meaning must have exactly ${REQUIRED_EXERCISE_COUNT} exercises.`,
    });
  }
  const ids = set.exercises.map((exercise) => exercise.id);
  if (!unique(ids)) {
    issues.push({ code: "duplicate_exercise_ids", message: "Exercise IDs must be unique." });
  }
  const mix = expectedMix(hasUsefulRelations);
  const counts = countByKind(set.exercises);
  for (const kind of Object.keys(mix) as Array<keyof typeof mix>) {
    if (counts[kind] !== mix[kind]) {
      issues.push({
        code: "exercise_mix",
        message: `Expected ${mix[kind]} ${kind} exercises, received ${counts[kind]}.`,
      });
    }
  }
  for (const exercise of set.exercises) {
    issues.push(...validateExercise(exercise));
    for (const referencedId of exercise.referencedMeaningIds) {
      if (referencedId !== set.meaningId && !findMeaningById(snapshot, referencedId)) {
        issues.push({
          code: "invalid_referenced_meaning",
          message: `Exercise ${exercise.id} references unknown meaning ${referencedId}.`,
        });
      }
    }
  }
  for (let i = 0; i < set.exercises.length; i += 1) {
    for (let j = i + 1; j < set.exercises.length; j += 1) {
      if (questionsLookNearDuplicate(set.exercises[i].question, set.exercises[j].question)) {
        issues.push({
          code: "near_duplicate_questions",
          message: `Questions ${set.exercises[i].id} and ${set.exercises[j].id} are too similar.`,
        });
      }
    }
  }
  return issues;
}

export function isMeaningPracticeReady(
  snapshot: VocabularySnapshot,
  meaning: MeaningCard,
): boolean {
  if (meaning.practiceReadiness !== "ready") {
    return false;
  }
  const set = snapshot.exerciseSets.find((item) => item.meaningId === meaning.id);
  if (!set || set.status !== "ready") {
    return false;
  }
  if (set.sourceContentRevision !== meaning.contentRevision) {
    return false;
  }
  return set.exercises.length === REQUIRED_EXERCISE_COUNT;
}

export function meaningHasConfusablePractice(
  snapshot: VocabularySnapshot,
  meaning: MeaningCard,
): boolean {
  if (!isMeaningPracticeReady(snapshot, meaning)) {
    return false;
  }
  if (!hasValidOutgoingRelation(snapshot, meaning.id)) {
    return false;
  }
  const set = snapshot.exerciseSets.find((item) => item.meaningId === meaning.id);
  return Boolean(set?.exercises.some((exercise) => exercise.kind === "confusable_context"));
}

export type PracticeExerciseMode = "due" | "extra" | "confusable" | "select";

export function isStandardAssessmentExercise(exercise: Exercise): boolean {
  return exercise.kind === "meaning_to_word";
}

export function flaggedExerciseIdsFor(
  snapshot: VocabularySnapshot,
  meaningId: string,
): ReadonlySet<string> {
  const state = snapshot.practiceStates.find((item) => item.meaningId === meaningId);
  return new Set(state?.flaggedExerciseIds ?? []);
}

export function eligibleExercisesForMode(
  snapshot: VocabularySnapshot,
  meaningId: string,
  mode: PracticeExerciseMode,
): Exercise[] {
  const set = snapshot.exerciseSets.find((item) => item.meaningId === meaningId);
  if (!set || set.status !== "ready") {
    return [];
  }
  const flagged = flaggedExerciseIdsFor(snapshot, meaningId);
  const available = set.exercises.filter((exercise) => !flagged.has(exercise.id));
  if (mode === "confusable") {
    return available.filter((exercise) => exercise.kind === "confusable_context");
  }
  const assessments = available.filter(isStandardAssessmentExercise);
  if (mode === "due" || mode === "extra") {
    return assessments;
  }
  return assessments.length > 0 ? assessments : available;
}

export function suitableExercisesForMode(
  snapshot: VocabularySnapshot,
  meaningId: string,
  mode: PracticeExerciseMode,
): Exercise[] {
  return eligibleExercisesForMode(snapshot, meaningId, mode);
}

export function meaningHasEligibleAssessment(
  snapshot: VocabularySnapshot,
  meaning: MeaningCard,
): boolean {
  return eligibleExercisesForMode(snapshot, meaning.id, "due").length > 0;
}

export function isMeaningUnavailableOverdue(
  snapshot: VocabularySnapshot,
  meaning: MeaningCard,
  now: string,
  isDue: (dueAt: string, now: string) => boolean,
): boolean {
  const state = snapshot.practiceStates.find((item) => item.meaningId === meaning.id);
  if (!state || !isDue(state.dueAt, now)) {
    return false;
  }
  return !isMeaningPracticeReady(snapshot, meaning) || !meaningHasEligibleAssessment(snapshot, meaning);
}
